import { useMemo, useState } from "react";
import { FileText, Loader2, Paperclip } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fmtDate, round2, vals } from "@/lib/format";
import { useFileMeta } from "@/lib/files";
import { openFile } from "@/components/files/FileFields";

function Thumb({ id }) {
  const meta = useFileMeta(id);
  if (!meta) return <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />;
  return meta.type.startsWith("image/") ? <img src={meta.url} alt="" className="w-full h-full object-cover" /> : <FileText className="w-10 h-10 text-[#C7353B]" strokeWidth={1.4} />;
}

export default function InvoiceGallery({ supplierId }) {
  const { view, t, money, lang } = useApp();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const all = vals(view.purchases).filter((p) => p.supplier_id === supplierId);
  const list = useMemo(() => all.filter((p) => p.attachment_id)
    .filter((p) => (!from || p.created_at.slice(0, 10) >= from) && (!to || p.created_at.slice(0, 10) <= to))
    .sort((a, b) => b.created_at.localeCompare(a.created_at)), [all, from, to]);
  const missing = all.filter((p) => !p.attachment_id).length;
  const total = round2(list.reduce((s, p) => s + (p.total || 0), 0));
  return (
    <div className="space-y-3" data-testid="invoice-gallery">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-bold text-muted-foreground">{t("date_range")}</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-lg border border-border px-2" data-testid="invoice-gallery-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-lg border border-border px-2" data-testid="invoice-gallery-to" />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-xl bg-secondary p-2"><div className="text-muted-foreground">{t("invoices")}</div><div className="text-lg font-extrabold" data-testid="invoice-gallery-count">{list.length}</div></div>
        <div className="rounded-xl bg-secondary p-2"><div className="text-muted-foreground">{t("total")}</div><div className="text-lg font-extrabold" data-testid="invoice-gallery-total">{money(total)}</div></div>
        <div className={`rounded-xl p-2 ${missing ? "bg-[#E67E22]/10 text-[#A85A12]" : "bg-[#E3F6EC] text-[#1F9D55]"}`}><div>{t("without_invoice")}</div><div className="text-lg font-extrabold" data-testid="invoice-gallery-missing">{missing}</div></div>
      </div>
      {list.length === 0 ? <p className="text-muted-foreground text-center py-6 flex items-center justify-center gap-2" data-testid="invoice-gallery-empty"><Paperclip className="w-4 h-4" />{t("no_invoices")}</p> : (
        <div className="grid grid-cols-2 gap-2">
          {list.map((p) => (
            <button key={p.id} type="button" onClick={() => openFile(p.attachment_id)} data-testid="invoice-gallery-item"
              className="rounded-xl border border-border bg-white overflow-hidden text-start hover:border-primary hover:shadow-md transition-[box-shadow,border-color]">
              <div className="h-28 bg-secondary grid place-items-center overflow-hidden"><Thumb id={p.attachment_id} /></div>
              <div className="p-2">
                <div className="text-xs text-muted-foreground">{fmtDate(p.created_at, lang)}</div>
                <div className="font-extrabold">{money(p.total)}</div>
                <div className="text-xs">{t("paid")}: {money(p.paid ?? p.total)}{p.total - (p.paid ?? p.total) > 0 && <span className="text-destructive"> · +{money(p.total - (p.paid ?? p.total))}</span>}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
